import axios from "axios";
import { DemoApiService } from "../../services/DemoApiService";
import frontendErrorLogger from "../../services/frontendErrorLogger";
import { getUserRole, getInventoryRole } from "../../utils/authStorage";

const API_URL = import.meta.env.VITE_API_URL;

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 90000, // 90 segundos para operaciones complejas como subida de archivos y emails
  withCredentials: true, // Enviar cookies httpOnly automáticamente
  headers: {
    Accept: "application/json",
  },
});

// Interceptor para configuración de solicitudes
apiClient.interceptors.request.use(
  async (config) => {
    const inventoryRequest = /^\/(equipos|inventario)(?:\/|\?|$)/.test(config.url || '');
    const role = getInventoryRole();
    if (inventoryRequest && getUserRole() === 5 && Number.isInteger(role) && role >= 1 && role <= 8) {
      config.headers['X-Inventory-Role'] = String(role);
    } else {
      delete config.headers['X-Inventory-Role'];
    }
    // Verificar si estamos en modo demo y debemos interceptar
    if (DemoApiService.isDemoMode()) {
      try {
        const originalCall = () => axios(config);
        const interceptedResponse = await DemoApiService.interceptAPI(
          originalCall,
          config,
        );

        // Si se interceptó exitosamente, devolver la respuesta mock
        if (interceptedResponse && interceptedResponse !== originalCall) {
          return Promise.reject({
            isIntercepted: true,
            response: interceptedResponse,
            config,
          });
        }
      } catch (interceptError) {
        // Si hay error en la interceptación, continuar con request normal
        console.log(
          "🎬 Demo interceptor error, continuando con request normal",
        );
      }
    }

    // Los tokens se envían automáticamente via cookies httpOnly (withCredentials: true)

    // Solo establecer Content-Type para solicitudes que no sean FormData
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    // Timeouts específicos por tipo de operación
    if (config.url && config.url.includes("/files")) {
      config.timeout = 120000; // 2 minutos para archivos
    } else if (
      config.url &&
      config.url.includes("cargar-firma-identificacion")
    ) {
      config.timeout = 150000; // 2.5 minutos para validación de documentos
    } else if (config.url && config.url.includes("send-email")) {
      config.timeout = 60000; // 1 minuto para emails
    } else if (config.url && config.url.includes("generar-qr-masivo")) {
      config.timeout = 600000; // 10 minutos para generación masiva de QR
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor para manejar respuestas y errores
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Manejar interceptación de demo
    if (error.isIntercepted && error.response) {
      console.log("🎬 Demo: Devolviendo respuesta interceptada");
      return error.response;
    }

    // Verificar si estamos en modo demo y se puede interceptar el error
    if (DemoApiService.isDemoMode() && error.config) {
      console.log("🎬 Demo Mode: Intentando recuperar de error con datos mock");
      try {
        // Para errores 403 en reportes de planta, usar datos mock directamente
        if (
          error.response?.status === 403 &&
          error.config.url?.includes("/reportes/planta/")
        ) {
          console.log(
            "🎬 Demo: Error 403 esperado para rol demo, usando datos mock",
          );
          const mockResponse = await DemoApiService.getMockReportsByPlant();
          return mockResponse;
        }

        // Intentar proporcionar datos mock en caso de otros errores
        const originalCall = () => Promise.reject(error);
        const mockResponse = await DemoApiService.interceptAPI(
          originalCall,
          error.config,
        );
        return mockResponse;
      } catch (demoError) {
        // Si los datos mock fallan, continuar con el error original
        console.log("❌ Demo fallback falló, usando error original");
      }
    }

    // Mejorar manejo de errores de timeout
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      console.error("❌ Request timeout:", error.config?.url);
      error.message =
        "La operación tardó demasiado tiempo. Por favor, intenta de nuevo.";
    }

    // Registrar errores de API en el sistema de logging
    // No registrar: 401 (redirección login), interceptados, o marcados con skipErrorLog
    if (
      error.response?.status !== 401 &&
      !error.isIntercepted &&
      !error.config?.skipErrorLog
    ) {
      try {
        frontendErrorLogger.logApiError(error, {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
        });
      } catch (logError) {
        console.error("Error al registrar error de API:", logError);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
